import { html } from 'lit';
import { customElement } from 'lit/decorators.js';

import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants } from './credit-quality-chart.constants.js';
import type { CreditQualityChartContentObject } from './credit-quality-chart.model.js';
import { Options } from 'highcharts';
import { BeaconChartBase } from '@trp-beacon-ui-kit/foundation/components';
import {
  formatToNDecimalPlaces,
  formatNumberToShowSign,
} from '../../common/utils/formatNumber.js';

/**
 * @tag pop-credit-quality-chart-component
 * @attribute { CreditQualityChartContentObject } [data-component-object] CreditQualityChart content object
 */
@customElement(Constants.TAG_NAME)
export class PopCreditQualityChartComponent extends PopBaseComponent {
  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * CreditQualityChart content object
   */
  componentObject?: CreditQualityChartContentObject;

  firstUpdated(): void {
    const chart = this.shadowRoot?.querySelector('beacon-bar-chart');

    if (chart) {
      // This is necessary because the pointFormatter option for the tooltip is a function and doesn't
      // work correctly when only passed in the options attribute to the beacon-bar-chart element.
      chart.options = this.getOptions();
    }
  }

  private getSeriesData() {
    const creditQualityData =
      this.componentObject?.itemList?.map((item) => ({
        name: item.qualityRating,
        y: item.percentOfFund,
        percentChange: item.percentOfChangeFromLastPeriod,
        benchmark: item.percentOfIndicativeBenchmark,
      })) || [];
    return [
      {
        name: Constants.CREDIT_RATING,
        data: creditQualityData,
      },
    ];
  }

  private getQualityRatings() {
    return (
      this.componentObject?.itemList?.map((item) => item.qualityRating) || []
    );
  }

  private getOptions(): Options {
    return {
      tooltip: {
        useHTML: true,
        headerFormat: BeaconChartBase.tooltipHeaderHTML('{point.key}'),
        pointFormatter: function () {
          return `
            ${BeaconChartBase.multiLevelTooltipPointHTML(Constants.PERCENT_OF_PORTFOLIO, `${formatToNDecimalPlaces(this.y, 2)}%`, true)}
            ${BeaconChartBase.multiLevelTooltipPointHTML(Constants.PERCENT_CHANGE, `${formatNumberToShowSign(this.percentChange, 2)}%`, true)}
            ${BeaconChartBase.multiLevelTooltipPointHTML(Constants.BENCHMARK, `${formatToNDecimalPlaces(this.benchmark, 1)}%`, true)}
          `;
        },
      },
      exporting: {
        enabled: false,
      },
    };
  }

  render() {
    return this.renderComponent(html`
      <section class="credit-quality-chart-content">
        <beacon-bar-chart
          variant="vertical"
          tooltip-variant="multi-level"
          y-axis-label="${Constants.PERCENT_OF_PORTFOLIO}"
          x-axis-label="${Constants.CREDIT_RATING}"
          series="${JSON.stringify(this.getSeriesData())}"
          categories="${this.getQualityRatings()}"
          options="${JSON.stringify(this.getOptions())}"
          disable-legend="true"
        >
        </beacon-bar-chart>
      </section>
    `);
  }
}
